import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { User, LogOut, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import NotificationBell from "@/components/marketplace/NotificationBell";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "@/integrations/supabase/client";
import { prefetchListings } from "@/lib/marketplace-prefetch";

const navLinks = [
  { label: "Rent",  path: "/rent-a-van" },
  { label: "Build", path: "/buy-a-van/personal" },
  { label: "Buy",   path: "/inventory" },
];


export const Header = () => {
  const [megaOpen, setMegaOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const getUser = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setUser(session?.user ?? null);
    };
    getUser();
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Close user menu on click outside
  useEffect(() => {
    if (!userMenuOpen) return;
    const handler = () => setUserMenuOpen(false);
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [userMenuOpen]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    setUserMenuOpen(false);
    navigate("/");
  };

  // Scroll-based nav compression. Threshold pushed to 80px so the
  // transparent header lingers across the first viewport on cream
  // marketplace pages — the cream glass then fades in as the renter
  // scrolls into the listing grid.
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Cream-themed routes — header sits over light backgrounds, so the
  // brand wordmark + nav must render in charcoal when at top of page.
  // Audited 2026-05: only these surfaces actually paint cream. The
  // previous list assumed every host/fleet-manager funnel page was
  // cream, but ManageAFleet, ListYourVan, HostAVan, RevenueCalculator,
  // FindFleetManager, FleetManagerApply, FleetManagerSetup and
  // FleetManagerProfile all render on the dark `bg-background` theme.
  // Listing them here made the charcoal nav text invisible against
  // their near-black heroes.
  //
  // 2026-05-27: extended for cream pages that wrap themselves in
  // <PageLayout> + an inline cream surface, where the global
  // bg-background never paints. Without these entries the ivory
  // wordmark was invisible against the page's #F5F0EB.
  const lightThemePaths = [
    "/rent-a-van",
    "/marketplace/",
    // Only the fleet-manager dashboard runs on the cream marketplace
    // surface; everything else under /fleet-manager (apply, setup,
    // profile, find-fleet-manager) is on the dark theme.
    "/fleet-manager/dashboard",
    // Bulk van onboarding renders cream via inline styles.
    "/fleet/bulk-onboard",
    // Host onboarding wizard + management-agreement signing — cream.
    "/host/onboarding",
    "/host/sign-management-agreement",
    // Cream landing pages.
    "/lp/rental",
    "/lp/host",
    "/lp/worldcup",
  ];
  const isLightPage = lightThemePaths.some((p) =>
    p.endsWith("/")
      ? location.pathname.startsWith(p)
      : location.pathname === p || location.pathname.startsWith(p + "/")
  );
  // Light-themed pages keep charcoal text in BOTH the transparent and
  // scrolled-glass states, because the scrolled glass on those pages is now
  // cream-tinted (not the dark glass used on dark routes).
  const overLightBg = isLightPage;

  return (
    <>
      <style>{`
        @keyframes pulse-orb {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(0.85); }
        }
      `}</style>
      <motion.header
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1, ease: [0.16, 1, 0.3, 1] }}
        className="px-6 md:px-12 lg:px-20"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 100,
          transition:
            'background-color 0.3s ease, backdrop-filter 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease',
          background: scrolled
            ? isLightPage
              ? 'rgba(245,240,235,0.92)' // cream-tinted glass on light routes
              : 'rgba(8,8,8,0.85)'        // dark glass on dark routes
            : 'transparent',
          backdropFilter: scrolled ? (isLightPage ? 'blur(16px)' : 'blur(20px)') : 'none',
          WebkitBackdropFilter: scrolled ? (isLightPage ? 'blur(16px)' : 'blur(20px)') : 'none',
          // Cream / marketplace routes: never a hard hairline between nav
          // and page — the blur + soft drop shadow do the separation.
          // Dark routes keep their existing bronze hairline.
          borderBottom: scrolled && !isLightPage
            ? '1px solid rgba(184,151,106,0.1)'
            : '1px solid transparent',
          boxShadow: scrolled && isLightPage ? '0 1px 24px rgba(44,36,32,0.08)' : 'none',
        }}
      >
        <div className={cn(
          "flex items-center justify-between transition-all duration-500",
          scrolled ? "h-16 md:h-[64px]" : "h-20 md:h-[88px]"
        )}>
          {/* Brand */}
          <Link
            to="/"
            onClick={() => window.scrollTo(0, 0)}
            className="font-serif text-[15px] md:text-[17px] tracking-[0.18em] uppercase"
            style={{ color: overLightBg ? "#2C2420" : "hsl(var(--foreground))" }}>
            <span className="hidden sm:inline">Vauren</span>
            <span className="sm:hidden font-serif text-lg tracking-[0.2em]">V</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-12">
            {navLinks.map((link) =>
              <Link
                key={link.path}
                to={link.path}
                onClick={() => { setMegaOpen(false); window.scrollTo(0, 0); }}
                onMouseEnter={link.path === "/rent-a-van" ? () => { void prefetchListings(); } : undefined}
                onFocus={link.path === "/rent-a-van" ? () => { void prefetchListings(); } : undefined}
                className="group relative text-[10px] tracking-[0.3em] uppercase transition-colors duration-300"
                style={{
                  color: overLightBg
                    ? (location.pathname === link.path ? "#2C2420" : "rgba(44,36,32,0.6)")
                    : (location.pathname === link.path ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))"),
                }}>
                {link.label}
                <span className="absolute -bottom-1.5 left-0 w-0 group-hover:w-full h-px bg-primary transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
              </Link>
            )}

            {/* "More" button — opens mega dropdown */}
            <button
              onClick={() => setMegaOpen(!megaOpen)}
              className="flex items-center gap-1 text-[10px] tracking-[0.3em] uppercase transition-colors duration-300"
              style={{ color: overLightBg ? "rgba(44,36,32,0.6)" : "hsl(var(--muted-foreground))" }}
            >
              More <ChevronDown size={12} className={`transition-transform duration-200 ${megaOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Auth area — exactly one of: signed-in cluster OR Sign In link */}
            <div className="ml-4 flex items-center gap-5">
              {user ? (
                <>
                  <NotificationBell userId={user.id} />
                  <div className="relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setUserMenuOpen(!userMenuOpen); }}
                      className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground hover:text-foreground transition-colors duration-300"
                    >
                      <User className="w-3.5 h-3.5" />
                      Account
                    </button>
                    <AnimatePresence>
                      {userMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: 8, scale: 0.97 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.97 }}
                          transition={{ duration: 0.2 }}
                          onClick={(e) => e.stopPropagation()}
                          className="absolute right-0 top-full mt-3 w-52 py-2 z-50"
                          style={{
                            background: '#FFFFFF',
                            border: '1px solid rgba(44,36,32,0.1)',
                            borderRadius: 8,
                            boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                          }}
                        >
                          <p
                            className="px-4 py-2 text-[10px] tracking-[0.15em] uppercase truncate mb-1"
                            style={{
                              color: 'rgba(44,36,32,0.55)',
                              borderBottom: '1px solid rgba(44,36,32,0.08)',
                            }}
                          >
                            {user.email}
                          </p>
                          <Link
                            to="/marketplace/dashboard"
                            onClick={() => { setUserMenuOpen(false); window.scrollTo(0, 0); }}
                            className="flex items-center gap-3 px-4 py-2.5 text-xs transition-colors"
                            style={{ color: '#2C2420' }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(44,36,32,0.05)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                            }}
                          >
                            <User className="w-3.5 h-3.5 text-primary" />
                            Dashboard
                          </Link>
                          <Link
                            to="/marketplace/messages"
                            onClick={() => { setUserMenuOpen(false); window.scrollTo(0, 0); }}
                            className="flex items-center gap-3 px-4 py-2.5 text-xs transition-colors"
                            style={{ color: '#2C2420' }}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.background = 'rgba(44,36,32,0.05)';
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.background = 'transparent';
                            }}
                          >
                            <svg className="w-3.5 h-3.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
                            Messages
                          </Link>
                          <button
                            onClick={handleSignOut}
                            className="flex items-center gap-3 px-4 py-2.5 text-xs w-full text-left transition-colors"
                            style={{ color: 'rgba(44,36,32,0.65)' }}
                            onMouseEnter={(e) => {
                              const el = e.currentTarget as HTMLButtonElement;
                              el.style.background = 'rgba(44,36,32,0.05)';
                              el.style.color = '#DC2626';
                            }}
                            onMouseLeave={(e) => {
                              const el = e.currentTarget as HTMLButtonElement;
                              el.style.background = 'transparent';
                              el.style.color = 'rgba(44,36,32,0.65)';
                            }}
                          >
                            <LogOut className="w-3.5 h-3.5" />
                            Sign Out
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </>
              ) : (
                <Link to="/marketplace/auth" className="text-xs tracking-widest uppercase text-white/60 hover:text-white transition-colors">
                  Sign In
                </Link>
              )}
            </div>
          </nav>

          {/* Mobile — V logo on the left (rendered above), bell + account
              on the right. The RENT/BUILD/BUY links plus the rest of the
              site are reachable via the More hamburger which opens the
              existing mega dropdown. Desktop still gets the inline nav.
              Spec note: keep the row tight and large-tap-target. */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={() => setMegaOpen(!megaOpen)}
              aria-label={megaOpen ? "Close menu" : "Open menu"}
              aria-expanded={megaOpen}
              className="flex items-center justify-center w-10 h-10 text-muted-foreground hover:text-foreground transition-colors duration-300"
              style={{ minHeight: 44, minWidth: 44 }}
            >
              {/* Three-line hamburger / rotates to X when open. */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                {megaOpen ? (
                  <>
                    <line x1="6" y1="6" x2="18" y2="18" />
                    <line x1="6" y1="18" x2="18" y2="6" />
                  </>
                ) : (
                  <>
                    <line x1="4" y1="7" x2="20" y2="7" />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="17" x2="20" y2="17" />
                  </>
                )}
              </svg>
            </button>
            {user ? (
              <>
                <NotificationBell userId={user.id} />
                <Link
                  to="/marketplace/dashboard"
                  aria-label="Account"
                  className="text-foreground flex items-center justify-center"
                  style={{ minHeight: 44, minWidth: 44 }}
                >
                  <User className="w-4 h-4" />
                </Link>
              </>
            ) : (
              <Link
                to="/marketplace/auth"
                aria-label="Sign in"
                className="text-foreground flex items-center justify-center"
                style={{ minHeight: 44, minWidth: 44 }}
              >
                <User className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>

        {/* Mega dropdown — desktop only */}
        <AnimatePresence>
          {megaOpen && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-30 top-[80px]"
                onClick={() => setMegaOpen(false)}
              />
              <motion.div
                className="absolute top-full left-0 right-0 z-40 border-b border-white/[0.08]"
                style={{
                  background: 'rgba(12, 10, 9, 0.92)',
                  backdropFilter: 'blur(24px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
                  maxHeight: 'calc(100vh - 60px)',
                  overflowY: 'auto',
                  WebkitOverflowScrolling: 'touch',
                }}
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.2 }}
              >
                <div className="h-px w-full bg-gradient-to-r from-transparent via-primary/40 to-transparent" />
                <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 grid grid-cols-1 md:grid-cols-3 gap-8">
                  {/* Column 1 — For Guests & Buyers */}
                  <div>
                    <p className="text-[9px] tracking-[0.4em] uppercase text-primary/60 mb-4">
                      For Guests &amp; Buyers
                    </p>
                    <div className="space-y-1">
                      {[
                        { label: "Browse Vans", path: "/rent-a-van", desc: "Book a luxury van by the day" },
                        { label: "Commission a Build", path: "/buy-a-van/personal", desc: "From $139,000" },
                        { label: "Buy a Van", path: "/inventory", desc: "Pre-built, ready to own" },
                        { label: "How It Works", path: "/how-it-works", desc: "The Vauren process" },
                      ].map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMegaOpen(false)}
                          className="group flex items-start gap-3 py-2.5 px-3 rounded-sm hover:bg-white/[0.08] transition-colors"
                        >
                          <div>
                            <p className="text-sm text-white/90 group-hover:text-white transition-colors font-light">{item.label}</p>
                            <p className="text-xs text-white/50 mt-0.5">{item.desc}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Column 2 — For Hosts & Operators */}
                  <div>
                    <p className="text-[9px] tracking-[0.4em] uppercase text-primary/60 mb-4">
                      For Hosts &amp; Operators
                    </p>
                    <div className="space-y-1">
                      {[
                        { label: "Host Your Van", path: "/host", desc: "Earn 80% of every booking" },
                        { label: "Manage a Fleet", path: "/manage-a-fleet", desc: "Fleet management tools" },
                        { label: "List Your Inventory", path: "/builders/apply", desc: "For dealers & builders" },
                        { label: "Revenue Calculator", path: "/host/revenue-calculator", desc: "Estimate your earnings" },
                      ].map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMegaOpen(false)}
                          className="group flex items-start gap-3 py-2.5 px-3 rounded-sm hover:bg-white/[0.08] transition-colors"
                        >
                          <div>
                            <p className="text-sm text-white/90 group-hover:text-white transition-colors font-light">{item.label}</p>
                            <p className="text-xs text-white/50 mt-0.5">{item.desc}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>

                  {/* Column 3 — Company */}
                  <div>
                    <p className="text-[9px] tracking-[0.4em] uppercase text-primary/60 mb-4">
                      Company
                    </p>
                    <div className="space-y-1">
                      {/* Vauren University — featured first with bronze pulse orb */}
                      <Link
                        to="/university"
                        onClick={() => setMegaOpen(false)}
                        className="group flex items-start gap-3 py-2.5 px-3 rounded-sm hover:bg-white/[0.08] transition-colors"
                        style={{ position: "relative" }}
                      >
                        <div style={{ position: "relative", flexShrink: 0, marginTop: 6 }}>
                          <div style={{
                            width: 8, height: 8, borderRadius: "50%",
                            background: "#B8976A",
                            boxShadow: "0 0 8px #B8976A, 0 0 16px rgba(184,151,106,0.4)",
                            animation: "pulse-orb 2s ease-in-out infinite",
                          }} />
                        </div>
                        <div>
                          <p className="text-sm font-light transition-colors duration-200" style={{ color: "#B8976A" }}>
                            Vauren University
                          </p>
                          <p className="text-xs transition-colors duration-200 mt-0.5" style={{ color: "rgba(255,255,255,0.5)" }}>
                            Free courses for hosts, renters &amp; buyers
                          </p>
                        </div>
                      </Link>
                      {[
                        { label: "About Vauren", path: "/about", desc: "Our story and mission" },
                        { label: "Blog", path: "/blog", desc: "Insights and guides" },
                        { label: "Builders", path: "/builders", desc: "Vauren Certified Builders" },
                        { label: "Insurance", path: "/insurance", desc: "Host coverage and security deposit" },
                        { label: "Contact", path: "/contact", desc: "Get in touch" },
                      ].map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMegaOpen(false)}
                          className="group flex items-start gap-3 py-2.5 px-3 rounded-sm hover:bg-white/[0.08] transition-colors"
                        >
                          <div>
                            <p className="text-sm text-white/90 group-hover:text-white transition-colors font-light">{item.label}</p>
                            <p className="text-xs text-white/50 mt-0.5">{item.desc}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </motion.header>
    </>);

};